(() => {
  const catalogRefreshers = new WeakMap();
  const debounce = (callback, delay = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), delay);
    };
  };

  const sectionUrl = (url, sectionId) => {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('section_id', sectionId);
    return next;
  };

  const parseSection = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector('[data-catalog-section]');
  };

  const normalizeFilter = (value) => value.trim().toLowerCase();

  const consolidateNativeOccasions = (form) => {
    const groups = new Map();
    form.querySelectorAll('input[type="checkbox"][name="filter.p.m.custom.occasion"]').forEach((input) => {
      if (input.closest('.tis-filter-fallback')) return;
      const key = normalizeFilter(input.value);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(input);
    });
    groups.forEach((inputs) => {
      if (inputs.length < 2) return;
      const primary = inputs.find((input) => input.value.trim() !== normalizeFilter(input.value)) || inputs[0];
      const originalDisabled = new Map(inputs.map((input) => [input, input.disabled]));
      const selectable = inputs.some((input) => !input.disabled || input.checked);
      const total = inputs.reduce((sum, input) => sum + (Number(input.closest('label')?.querySelector('small')?.textContent.replace(/,/g, '').trim()) || 0), 0);
      const count = primary.closest('label')?.querySelector('small');
      if (count) count.textContent = String(total);
      inputs.forEach((input) => {
        const label = input.closest('label');
        if (label) label.hidden = input !== primary;
      });
      const syncChecked = (checked) => {
        inputs.forEach((input) => {
          input.checked = checked;
          input.disabled = checked ? false : input === primary ? !selectable : originalDisabled.get(input);
        });
      };
      syncChecked(inputs.some((input) => input.checked));
      // The original change bubbles to the existing form handler after all
      // native values are synchronized; no synthetic events or extra requests.
      inputs.forEach((input) => input.addEventListener('change', () => syncChecked(input.checked)));
    });
  };

  const applyFallbackFilters = (catalog) => {
    const useFallback = Boolean(catalog.querySelector('.tis-filter-fallback'));
    const params = new URLSearchParams(window.location.search);
    const groups = {
      'filter.p.m.custom.is_best_seller': ['filterBestSeller'],
      'filter.p.m.custom.occasion': ['filterOccasion'],
      'filter.p.m.custom.theme': ['filterTheme'],
      'filter.p.m.custom.color_family': ['filterColor'],
      'filter.p.m.custom.recipient': ['filterRecipient'],
      'filter.p.m.custom.format_features': ['filterFeatures'],
    };
    const selected = Object.fromEntries(Object.keys(groups).map((name) => [name, [...new Set(params.getAll(name).map(normalizeFilter).filter(Boolean))]]));
    const cards = [...catalog.querySelectorAll('[data-product-card]')];
    let visible = 0;
    cards.forEach((card) => {
      const matches = !useFallback || Object.entries(groups).every(([name, dataKeys]) => {
        const values = selected[name];
        if (!values.length) return true;
        return values.some((value) => dataKeys.some((key) => (card.dataset[key] || '').split('|').map(normalizeFilter).includes(value)));
      });
      card.hidden = !matches;
      if (matches) visible += 1;
    });
    const resultText = catalog.querySelector('.tis-results-bar span:first-child');
    const countText = `Showing ${visible} ${visible === 1 ? 'design' : 'designs'}`;
    if (resultText) resultText.textContent = countText;
    const mobileCount = catalog.querySelector('.tis-mobile-filter-bar [aria-live]');
    if (mobileCount) mobileCount.textContent = countText;
    const empty = catalog.querySelector('[data-catalog-empty]');
    if (empty) empty.hidden = visible > 0;
  };

  const syncCategoryRail = (catalog) => {
    const rail = catalog.querySelector('[data-category-rail]');
    if (!rail) return;
    const params = new URLSearchParams(window.location.search);
    const activeCategories = params.getAll('filter.p.m.custom.occasion').map(normalizeFilter).filter(Boolean);
    rail.querySelectorAll('[data-category-link]').forEach((link) => {
      const value = normalizeFilter(link.dataset.categoryValue || '');
      const isActive = value ? activeCategories.includes(value) : activeCategories.length === 0;
      link.classList.toggle('is-active', isActive);
      if (isActive) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  };

  const syncCatalogHeading = (catalog) => {
    const heading = catalog.querySelector('[data-catalog-heading]');
    if (!heading) return;
    const context = catalog.querySelector('[data-catalog-context]');
    const params = new URLSearchParams(window.location.search);
    const categories = [...new Map(params.getAll('filter.p.m.custom.occasion').filter(Boolean).map((value) => [normalizeFilter(value), value])).values()];
    const defaultHeading = heading.dataset.defaultHeading || heading.textContent.trim() || 'Products';
    const defaultContext = context?.dataset.defaultContext || context?.textContent.trim() || '';
    heading.dataset.defaultHeading = defaultHeading;
    if (context) context.dataset.defaultContext = defaultContext;
    const labels = categories.map((category) => [...catalog.querySelectorAll('[data-category-link]')].find((link) => normalizeFilter(link.dataset.categoryValue || '') === normalizeFilter(category))?.dataset.categoryValue || category);
    const label = labels.length ? labels.join(', ') : defaultHeading;
    heading.textContent = label;
    if (context && defaultContext) {
      context.textContent = categories.length ? `Viewing products in ${label}` : defaultContext;
    }
  };

  const initializeCatalog = (root = document) => {
    const catalog = root.matches?.('[data-catalog-section]') ? root : root.querySelector?.('[data-catalog-section]');
    if (!catalog || catalog.dataset.tisInitialized === 'true') return;
    catalog.dataset.tisInitialized = 'true';
    const sectionId = catalog.dataset.sectionId;
    applyFallbackFilters(catalog);
    syncCategoryRail(catalog);
    syncCatalogHeading(catalog);

    let latestRequest = 0;
    const refresh = async (url, push = true, restoreY = 0) => {
      const request = ++latestRequest;
      catalog.setAttribute('aria-busy', 'true');
      const errorStatus = catalog.querySelector('[data-catalog-error]');
      if (errorStatus) errorStatus.hidden = true;
      try {
        const response = await fetch(sectionUrl(url, sectionId), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
        const next = parseSection(await response.text());
        if (request !== latestRequest || !catalog.isConnected) return;
        if (!next) throw new Error('Catalog section was not returned');
        if (push) {
          history.replaceState({ ...(history.state || {}), scrollY: window.scrollY }, '', window.location.href);
          history.pushState({ scrollY: 0 }, '', url);
        }
        catalog.replaceWith(next);
        initializeCatalog(next);
        initializePredictiveSearch(next);
        document.dispatchEvent(new CustomEvent('tis:catalog:updated', { detail: { catalog: next } }));
        const live = next.querySelector('[aria-live]');
        if (live) live.setAttribute('aria-live', 'polite');
        window.scrollTo({ top: restoreY, behavior: 'instant' });
      } catch (error) {
        if (request !== latestRequest || !catalog.isConnected) return;
        catalog.removeAttribute('aria-busy');
        if (errorStatus) errorStatus.hidden = false;
        window.dispatchEvent(new CustomEvent('tis:catalog-error', { detail: error }));
      }
    };
    catalogRefreshers.set(catalog, refresh);

    const updateFromForm = (form) => {
      const params = new URLSearchParams(new FormData(form));
      [...params.entries()].forEach(([key, value]) => { if (!value) params.delete(key); });
      const url = new URL(form.action, window.location.origin);
      url.search = params.toString();
      refresh(url);
    };
    const syncFallbackFilters = (form) => {
      const params = new URLSearchParams(window.location.search);
      form.querySelectorAll('.tis-filter-fallback input[type="checkbox"]').forEach((input) => {
        input.checked = params.getAll(input.name).map(normalizeFilter).includes(normalizeFilter(input.value));
      });
    };
    const delayedUpdate = debounce((form) => updateFromForm(form), 250);

    catalog.querySelectorAll('[data-filter-form]').forEach((form) => {
      syncFallbackFilters(form);
      consolidateNativeOccasions(form);
      const isDialogForm = Boolean(form.closest('[data-filter-dialog]'));
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        form.closest('[data-filter-dialog]')?.close();
        updateFromForm(form);
      });
      form.addEventListener('change', (event) => {
        if (isDialogForm) return;
        if (event.target.matches('input[data-predictive-search-field]')) return;
        if (event.target.matches('input[type="checkbox"], select')) updateFromForm(form);
        else delayedUpdate(form);
      });
      form.addEventListener('input', (event) => {
        if (isDialogForm) return;
        if (event.target.matches('input[data-predictive-search-field]')) return;
        if (!event.target.matches('input[type="checkbox"]')) delayedUpdate(form);
      });
    });

    const dialog = catalog.querySelector('[data-filter-dialog]');
    let filterDialogScrollY = 0;
    let previousBodyTop = '';
    const lockFilterPageScroll = () => {
      if (document.body.classList.contains('tis-filter-dialog-open')) return;
      filterDialogScrollY = window.scrollY;
      previousBodyTop = document.body.style.top;
      document.documentElement.classList.add('tis-filter-dialog-open');
      document.body.classList.add('tis-filter-dialog-open');
      document.body.style.top = `-${filterDialogScrollY}px`;
    };
    const unlockFilterPageScroll = () => {
      if (!document.body.classList.contains('tis-filter-dialog-open')) return;
      document.documentElement.classList.remove('tis-filter-dialog-open');
      document.body.classList.remove('tis-filter-dialog-open');
      document.body.style.top = previousBodyTop;
      window.scrollTo({ top: filterDialogScrollY, behavior: 'instant' });
    };
    dialog?.addEventListener('close', unlockFilterPageScroll);
    catalog.querySelector('[data-open-filters]')?.addEventListener('click', () => {
      if (!dialog) return;
      dialog.showModal();
      lockFilterPageScroll();
    });
    catalog.querySelectorAll('[data-close-filters]').forEach((button) => button.addEventListener('click', () => dialog?.close()));
    catalog.querySelector('[data-apply-filters]')?.addEventListener('click', () => {
      const form = dialog?.querySelector('[data-filter-form]');
      if (form) {
        dialog?.close();
        updateFromForm(form);
      }
    });

    catalog.querySelectorAll('[data-category-link]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        const destinationParams = new URLSearchParams(url.search);
        url.search = window.location.search;
        url.searchParams.delete('filter.p.m.custom.occasion');
        [...new Set(destinationParams.keys())].forEach((key) => {
          url.searchParams.delete(key);
          destinationParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
        });
        url.searchParams.delete('page');
        refresh(url);
      });
    });

    catalog.querySelector('[data-load-more]')?.addEventListener('click', async (event) => {
      event.preventDefault();
      const button = event.currentTarget;
      button.setAttribute('aria-busy', 'true');
      const errorStatus = catalog.querySelector('[data-catalog-error]');
      if (errorStatus) errorStatus.hidden = true;
      try {
        const response = await fetch(sectionUrl(button.href, sectionId));
        if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
        const next = parseSection(await response.text());
        if (!next) throw new Error('Catalog section was not returned');
        const grid = catalog.querySelector('[data-product-grid]');
        next.querySelectorAll('[data-product-grid] > *').forEach((card) => grid?.append(card));
        const nextButton = next.querySelector('[data-load-more]');
        if (nextButton) button.href = nextButton.href;
        else button.remove();
        applyFallbackFilters(catalog);
        document.dispatchEvent(new CustomEvent('tis:catalog:updated', { detail: { catalog } }));
      } catch (error) {
        if (errorStatus) errorStatus.hidden = false;
        window.dispatchEvent(new CustomEvent('tis:catalog-error', { detail: error }));
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  };

  const initializePredictiveSearch = (root = document) => {
    root.querySelectorAll('[data-predictive-search]:not([data-tis-initialized])').forEach((container) => {
      container.dataset.tisInitialized = 'true';
      const input = container.querySelector('input[type="search"]');
      const results = container.querySelector('[data-predictive-results]');
      const searchAction = container.dataset.searchAction;
      let searchTimer;
      let searchController;
      let searchRevision = 0;
      if (!input || !results) return;
      if (!results.id) results.id = `${input.id || 'TisSearch'}-suggestions`;
      input.setAttribute('aria-controls', results.id);
      const hideSuggestions = () => {
        clearTimeout(searchTimer);
        searchTimer = undefined;
        searchController?.abort();
        searchRevision += 1;
        results.hidden = true;
        results.replaceChildren();
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-busy');
      };
      const search = async (revision, query) => {
        searchController = new AbortController();
        input.setAttribute('aria-busy', 'true');
        try {
          const endpoint = new URL(container.dataset.searchUrl || `${window.Shopify?.routes?.root || '/'}search/suggest`, window.location.origin);
          endpoint.searchParams.set('q', query);
          endpoint.searchParams.set('resources[type]', 'product');
          endpoint.searchParams.set('resources[limit]', '5');
          endpoint.searchParams.set('section_id', 'tis-predictive-search');
          const response = await fetch(endpoint, { signal: searchController.signal });
          if (!response.ok) throw new Error('Search suggestions unavailable');
          const html = await response.text();
          if (revision !== searchRevision || !container.isConnected) return;
          results.innerHTML = html;
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        } catch (error) {
          if (revision !== searchRevision || error.name === 'AbortError') return;
          const status = document.createElement('p');
          status.setAttribute('role', 'status');
          status.textContent = container.dataset.searchError || 'Suggestions could not load. Press Search to see all results.';
          results.replaceChildren(status);
          results.hidden = false;
          input.setAttribute('aria-expanded', 'false');
        } finally {
          if (revision === searchRevision) input.removeAttribute('aria-busy');
        }
      };
      const scheduleSearch = (event) => {
        hideSuggestions();
        if (event?.isComposing) return;
        const query = input.value.trim();
        if (query.length < 2) return;
        const revision = searchRevision;
        searchTimer = setTimeout(() => {
          searchTimer = undefined;
          search(revision, query);
        }, 250);
      };
      input.addEventListener('input', scheduleSearch);
      input.addEventListener('compositionend', scheduleSearch);
      container.addEventListener('click', (event) => {
        if (!event.target.closest('[data-close-suggestions]')) return;
        hideSuggestions();
        input.focus({ preventScroll: true });
      });
      container.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && (!results.hidden || input.hasAttribute('aria-busy') || searchTimer !== undefined)) {
          event.preventDefault();
          event.stopPropagation();
          hideSuggestions();
          input.focus({ preventScroll: true });
        } else if (event.key === 'Escape' && container.closest('dialog')?.open) {
          // Native search inputs can consume Escape to clear text before a dialog cancels.
          event.preventDefault();
          event.stopPropagation();
          container.closest('dialog').close();
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || results.hidden) return;
        const links = [...results.querySelectorAll('.tis-predictive-item')];
        if (!links.length) return;
        event.preventDefault();
        const current = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? (current + 1) % links.length : (current <= 0 ? links.length - 1 : current - 1);
        links.forEach((link, index) => link.setAttribute('aria-selected', String(index === next)));
        links[next].focus({ preventScroll: true });
      });
      container.closest('dialog')?.addEventListener('close', hideSuggestions);
      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && searchAction) {
          event.preventDefault();
          const query = input.value.trim();
          if (!query) return;
          const url = new URL(searchAction, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('type', 'product');
          url.searchParams.set('options[prefix]', 'last');
          window.location.assign(url.toString());
          return;
        }
      });
    });
  };

  const openQuickView = async (button) => {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.parentElement.querySelector('[data-quick-view-error]')?.remove();
    try {
      const url = new URL(button.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', 'tis-quick-view');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
      const template = document.createElement('template');
      template.innerHTML = (await response.text()).trim();
      const dialog = template.content.querySelector('[data-quick-view-dialog]');
      if (!dialog) throw new Error('Quick view was not returned');
      document.querySelector('[data-quick-view-dialog]')?.remove();
      document.body.append(dialog);
      const closeButton = dialog.querySelector('[data-close-quick-view]');
      closeButton?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('close', () => { dialog.remove(); if (button.isConnected) button.focus(); }, { once: true });
      dialog.showModal();
      document.dispatchEvent(new CustomEvent('tis:preview-opened', { detail: { productPath: button.dataset.productUrl, productId: dialog.dataset.tisProductId } }));
      closeButton?.focus();
    } catch (error) {
      const status = document.createElement('p');
      status.dataset.quickViewError = '';
      status.setAttribute('role', 'status');
      status.textContent = 'Quick view is unavailable. ';
      const link = document.createElement('a');
      link.href = button.dataset.productUrl;
      link.textContent = 'View product details';
      status.append(link);
      button.insertAdjacentElement('afterend', status);
      if (button.isConnected) button.focus();
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quick-view]');
    if (button) { event.preventDefault(); openQuickView(button); }
  });
  window.addEventListener('popstate', (event) => {
    const catalog = document.querySelector('[data-catalog-section]');
    if (!catalog) return;
    catalogRefreshers.get(catalog)?.(window.location.href, false, event.state?.scrollY || 0);
  });
  initializeCatalog();
  initializePredictiveSearch();
  document.addEventListener('shopify:section:load', (event) => { initializeCatalog(event.target); initializePredictiveSearch(event.target); });
})();
